import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  eventTimelineDtoSchema,
  venueTimelineTemplateDtoSchema,
  venueTimelineTemplateListDtoSchema,
  type EventTimelineDto,
  type VenueTimelineTemplateDto,
  type VenueTimelineTemplateListDto,
} from '@lib/dto/event-timeline.dto'
import type {
  ApplyVenueTimelineTemplateInput,
  CreateEventTimelineEntryInput,
  CreateVenueTimelineTemplateInput,
  UpdateEventTimelineEntryInput,
  UpdateVenueTimelineTemplateInput,
} from '@lib/input/event-timeline.input'

/** A venue's timeline templates — the shapes its days usually take. */
class VenueTimelineService {
  async list(venueId: string): Promise<VenueTimelineTemplateListDto> {
    try {
      return await api.get(`/venues/${venueId}/timeline-templates`, venueTimelineTemplateListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list timeline templates', { venueId }, error)
      throw error
    }
  }

  async create(venueId: string, input: CreateVenueTimelineTemplateInput): Promise<VenueTimelineTemplateDto> {
    try {
      return await api.post(`/venues/${venueId}/timeline-templates`, input, venueTimelineTemplateDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create timeline template', { venueId }, error)
      throw error
    }
  }

  async update(
    venueId: string,
    templateId: string,
    input: UpdateVenueTimelineTemplateInput,
  ): Promise<VenueTimelineTemplateDto> {
    try {
      return await api.patch(
        `/venues/${venueId}/timeline-templates/${templateId}`,
        input,
        venueTimelineTemplateDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to update timeline template', { venueId, templateId }, error)
      throw error
    }
  }

  /** Returns what is left, so a list is right without a second round trip. */
  async remove(venueId: string, templateId: string): Promise<VenueTimelineTemplateListDto> {
    try {
      return await api.delFor(
        `/venues/${venueId}/timeline-templates/${templateId}`,
        venueTimelineTemplateListDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to delete timeline template', { venueId, templateId }, error)
      throw error
    }
  }
}

/** An event's run of the day. Every call answers with the whole timeline. */
class EventTimelineService {
  async get(eventId: string): Promise<EventTimelineDto> {
    try {
      return await api.get(`/events/${eventId}/timeline`, eventTimelineDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load timeline', { eventId }, error)
      throw error
    }
  }

  async add(eventId: string, input: CreateEventTimelineEntryInput): Promise<EventTimelineDto> {
    try {
      return await api.post(`/events/${eventId}/timeline`, input, eventTimelineDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to add a timeline entry', { eventId }, error)
      throw error
    }
  }

  async update(eventId: string, entryId: string, input: UpdateEventTimelineEntryInput): Promise<EventTimelineDto> {
    try {
      return await api.patch(`/events/${eventId}/timeline/${entryId}`, input, eventTimelineDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update a timeline entry', { eventId, entryId }, error)
      throw error
    }
  }

  async remove(eventId: string, entryId: string): Promise<EventTimelineDto> {
    try {
      return await api.delFor(`/events/${eventId}/timeline/${entryId}`, eventTimelineDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to remove a timeline entry', { eventId, entryId }, error)
      throw error
    }
  }

  async apply(eventId: string, input: ApplyVenueTimelineTemplateInput): Promise<EventTimelineDto> {
    try {
      return await api.post(`/events/${eventId}/timeline/apply`, input, eventTimelineDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to apply a timeline template', { eventId }, error)
      throw error
    }
  }
}

export const venueTimelineService = new VenueTimelineService()
export const eventTimelineService = new EventTimelineService()
